import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useAuth } from '../../shared/auth/useAuth';
import { loginSchema, type LoginFormValues } from './login.schema';
import type { AuthUser } from '../../shared/auth/types';
import { BrandLogo } from '../../shared/components/BrandLogo';
import { BRAND } from '../../shared/brand';

interface LocationState {
  from?: string;
}

function homeFor(user: AuthUser): string {
  return user.perfil === 'ADMINISTRATIVO' ? '/' : '/me';
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const { status, user, login, lastLogoutReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  // Foco automático no campo usuário ao montar.
  useEffect(() => {
    setFocus('username');
  }, [setFocus]);

  // Já autenticado? Redireciona para a home do perfil.
  if (status === 'authenticated' && user) {
    const fallback = homeFor(user);
    const from = (location.state as LocationState | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : fallback} replace />;
  }

  async function onSubmit(values: LoginFormValues): Promise<void> {
    setSubmitError(null);
    try {
      const u = await login(values.username, values.password);
      const fallback = homeFor(u);
      const from = (location.state as LocationState | null)?.from;
      navigate(from && from !== '/login' ? from : fallback, { replace: true });
    } catch {
      // Mensagem genérica (anti-enumeração) — não distingue usuário/senha.
      setSubmitError(t('login.errorGeneric'));
      setFocus('username');
    }
  }

  const expiredNotice = lastLogoutReason === 'session-expired' && !submitError;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Painel de destaque (desktop). A foto vem de `public/brand`; se faltar,
          o gradiente da marca segura o visual sem imagem quebrada. */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          flex: 1,
          position: 'relative',
          bgcolor: 'primary.dark',
          backgroundImage: `url(${BRAND.loginHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(135deg, rgba(46,49,146,0.88) 0%, rgba(41,171,226,0.55) 100%)',
          }}
        />
      </Box>

      {/* Lado do formulário */}
      <Box
        sx={{
          flex: { xs: 1, md: '0 0 460px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 6 },
          bgcolor: 'background.default',
        }}
      >
        <Paper elevation={2} sx={{ p: 4, width: '100%', maxWidth: 380 }}>
          <Stack spacing={3}>
            <Stack alignItems="center" spacing={2} sx={{ textAlign: 'center' }}>
              <BrandLogo
                src={BRAND.logo}
                alt={t('app.name')}
                height={{ xs: 60, sm: 72 }}
                fallbackColor="primary.main"
              />
              <Typography variant="body2" color="text.secondary">
                {t('login.subtitle')}
              </Typography>
            </Stack>

            {expiredNotice && (
              <Alert severity="info" role="status">
                {t('login.sessionExpired')}
              </Alert>
            )}

            {submitError && (
              <Alert severity="error" role="alert">
                {submitError}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2}>
                <TextField
                  label={t('login.username')}
                  autoComplete="username"
                  fullWidth
                  required
                  error={Boolean(errors.username)}
                  helperText={errors.username ? t(`login.errors.${errors.username.message}`) : ' '}
                  {...register('username')}
                  inputProps={{ 'aria-required': 'true' }}
                />
                <TextField
                  label={t('login.password')}
                  type="password"
                  autoComplete="current-password"
                  fullWidth
                  required
                  error={Boolean(errors.password)}
                  helperText={errors.password ? t(`login.errors.${errors.password.message}`) : ' '}
                  {...register('password')}
                  inputProps={{ 'aria-required': 'true' }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('login.submitting') : t('login.submit')}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
